(function (window) {
  'use strict';

  var MOULDING_WIDTH_IN = 2;
  var GOLD_LIP_RATIO = 0.22;
  var MAX_UPLOAD_BYTES = 8 * 1024 * 1024;
  var ARTWORK_WINDOW_FILL = '#e9e4da';
  // The reference photo is a single mitered corner. We take a thin cross-section
  // slice of each straight arm (outer edge -> inner gold lip, trimmed to exclude
  // the white background) and stretch it along each side, clipping each side to a
  // 45deg miter trapezoid so adjacent sides meet on a clean diagonal (no splice).
  // Measured from the photo: moulding spans 0.077 (outer edge) -> 0.373 (inner
  // gold lip), then the white opening begins. Slices stop at 0.368 to exclude it.
  var MOULDING_SRC = {
    // vertical cross-section slice of the horizontal (top) arm: outer top -> gold lip
    topSlice: { x: 0.66, y: 0.080, w: 0.04, h: 0.288 },
    // horizontal cross-section slice of the vertical (left) arm: outer left -> gold lip
    leftSlice: { x: 0.080, y: 0.66, w: 0.288, h: 0.04 }
  };

  var canvas = null;
  var container = null;
  var externalEl = null;
  var matNoteEl = null;
  var fileInput = null;
  var removeBtn = null;
  var matCheckbox = null;
  var matOptionsWrap = null;
  var matBorderSelect = null;
  var matColorSelect = null;
  var fitCheckbox = null;
  var fitSuggestEl = null;
  var fitSuggestTextEl = null;
  var fitApplyBtn = null;
  var suggestedDims = null;
  var lastChangedDim = null;
  var matEnlargeEl = null;
  var matEnlargeTextEl = null;
  var matEnlargeBtn = null;
  var matEnlargeTarget = null;
  var desiredVisibleH = null;
  var desiredVisibleW = null;
  // Artwork size (inches) captured when the mat is turned on. The ordered frame
  // is grown to matBase + mat border on each side; on un-tick we revert to it.
  var matBaseH = null;
  var matBaseW = null;
  // Size basis: how the entered dimensions are interpreted.
  //   'artwork' (default) — entered size is the ARTWORK; adding a mat grows the
  //     frame opening to fit art + mat (current behavior).
  //   'opening'           — entered size IS the fixed frame opening; a mat does
  //     NOT grow the frame, it insets and reduces the visible window.
  // Non-mat behavior is identical in both modes.
  var SIZE_BASIS = 'artwork';
  function usesEnlarge() {
    return SIZE_BASIS !== 'opening';
  }

  // Kit add-ons treat the DPO size fields as the customer's ARTWORK size and
  // draw the mat around that size. Writing a grown opening back into DPO was
  // racing (only one side would update) and made 5x5 jump to 5x7 / 7x7.
  function kitOwnsArtworkSize() {
    return !!document.getElementById('ff-kit-addons');
  }
  var skipProgrammaticDimUpdate = false;
  var badgeEl = null;
  var productPhotoEl = null;
  var productPhotoImg = null;
  var productPhotoBackBtn = null;
  var galleryEl = null;
  var showingProductPhoto = false;
  var resizeTimer = null;
  var lastHeight = null;
  var lastWidth = null;
  var artworkImage = null;
  var mouldingTextureImage = null;
  var mouldingTextureUrl = null;
  // Photographic canvas-weave texture used to paint the (empty) floater canvas.
  var canvasTextureImage = null;
  // Strip mode: the texture is a single rectified cross-section strip produced by
  // Moulding Studio (long axis = moulding length, short axis = face outer->lip),
  // rather than the legacy hand-tuned corner photo. Enabled per product via
  // data-moulding-format="strip". Legacy corner-photo behavior is unchanged.
  var STRIP_MODE = false;
  var rotatedStripCanvas = null;
  // Directional mouldings can't be mirror-tiled (the flip looks wrong), so we tile
  // straight when NO_MIRROR is set (fed from Odoo via the no_mirror metafield).
  var NO_MIRROR = false;
  // Floater frames: the canvas floats in a tray with a small gap, no lip over art.
  var IS_FLOATER = false;
  var FLOATER_FACE_IN = 0.4; // visible wall width beside the canvas
  var FLOATER_GAP_IN = 0.25; // dark float gap between wall and canvas
  // Rabbet lip: how much the frame edge overlaps the art (visible opening math).
  var FRAME_LIP_IN = 0.25;
  // No real moulding photo yet -> draw a neutral "representative" gray frame.
  var MISSING_TEXTURE = false;
  // 1/8" per side is the standard mat overlap so the art can't fall through.
  var MAT_OVERLAP_IN = 0.125;
  var matOverlapCheckbox = null;
  var summaryEl = null;

  // Snap a measurement to the nearest 1/16" — the finest increment we quote in
  // Odoo. Keeps the display in clean shop fractions instead of the raw decimals
  // we measure with (e.g. a 2.97" face shows as 3", not 2.97").
  function roundTo16(inches) {
    return Math.round(inches * 16) / 16;
  }

  // Format inches as a whole number + reduced sixteenth fraction (e.g. 20-1/2",
  // 2-15/16", 1/8"). Never shows a raw decimal and never prefixes a bare
  // fraction with "0-".
  function formatInchesLabel(inches) {
    if (!inches || inches <= 0) {
      return '';
    }

    var sixteenths = Math.round(inches * 16);
    var whole = Math.floor(sixteenths / 16);
    var rem = sixteenths - whole * 16;

    if (rem === 0) {
      return whole + '"';
    }

    var num = rem;
    var den = 16;

    while (num % 2 === 0 && den % 2 === 0) {
      num /= 2;
      den /= 2;
    }

    var fracText = num + '/' + den;

    return whole > 0 ? whole + '-' + fracText + '"' : fracText + '"';
  }

  function externalDimensions(heightIn, widthIn) {
    // Compute from the sixteenth-rounded face width so the outside numbers add
    // up cleanly with the displayed frame width (e.g. 14-1/2 + 3 + 3 = 20-1/2").
    var faceIn = roundTo16(MOULDING_WIDTH_IN);
    return {
      height: heightIn + faceIn * 2,
      width: widthIn + faceIn * 2
    };
  }

  function isDarkHex(hex) {
    if (!hex) {
      return false;
    }

    var c = hex.replace('#', '');

    if (c.length === 3) {
      c = c
        .split('')
        .map(function (ch) {
          return ch + ch;
        })
        .join('');
    }

    var r = parseInt(c.substring(0, 2), 16);
    var g = parseInt(c.substring(2, 4), 16);
    var b = parseInt(c.substring(4, 6), 16);
    var luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;

    return luminance < 0.45;
  }

  function getMatColor() {
    if (!matColorSelect) {
      return { hex: '#f5f0e8', name: 'Cream', dark: false };
    }

    var option = matColorSelect.options[matColorSelect.selectedIndex];
    var hex = matColorSelect.value || '#f5f0e8';
    var name = option ? option.textContent.trim() : 'Cream';

    return {
      hex: hex,
      name: name,
      dark: isDarkHex(hex)
    };
  }

  function matOverlapIn() {
    // The 1/8"-per-side overlap that keeps the art from falling through the mat
    // window. On by default; the customer can turn it off (e.g. to keep a
    // signature fully visible).
    return matOverlapCheckbox && !matOverlapCheckbox.checked ? 0 : MAT_OVERLAP_IN;
  }

  function getMatState(heightIn, widthIn) {
    var enabled = matCheckbox && matCheckbox.checked;
    var borderIn = matBorderSelect ? parseFloat(matBorderSelect.value) : 0;
    var color = getMatColor();

    if (!enabled || !borderIn || borderIn <= 0) {
      return {
        enabled: false,
        borderIn: 0,
        overlapIn: 0,
        openingHeightIn: heightIn,
        openingWidthIn: widthIn,
        artHeightIn: heightIn,
        artWidthIn: widthIn,
        windowHeightIn: heightIn,
        windowWidthIn: widthIn,
        valid: true,
        color: color
      };
    }

    var overlapIn = matOverlapIn();
    var openingHeightIn;
    var openingWidthIn;
    var artHeightIn;
    var artWidthIn;
    var windowHeightIn;
    var windowWidthIn;

    if (kitOwnsArtworkSize()) {
      // Entered size is artwork. Mat sits around it; opening grows; window is
      // the art minus the hold-in overlap. Dropdowns stay at the artwork size.
      artHeightIn = heightIn;
      artWidthIn = widthIn;
      openingHeightIn = heightIn + borderIn * 2;
      openingWidthIn = widthIn + borderIn * 2;
      windowHeightIn = heightIn - overlapIn * 2;
      windowWidthIn = widthIn - overlapIn * 2;
    } else {
      // Legacy: entered size is the frame opening; mat insets from it.
      openingHeightIn = heightIn;
      openingWidthIn = widthIn;
      windowHeightIn = heightIn - borderIn * 2 - overlapIn * 2;
      windowWidthIn = widthIn - borderIn * 2 - overlapIn * 2;
      artHeightIn = heightIn - borderIn * 2;
      artWidthIn = widthIn - borderIn * 2;
    }

    var valid = windowHeightIn > 0.1 && windowWidthIn > 0.1;

    return {
      enabled: true,
      borderIn: borderIn,
      overlapIn: overlapIn,
      openingHeightIn: openingHeightIn,
      openingWidthIn: openingWidthIn,
      artHeightIn: artHeightIn,
      artWidthIn: artWidthIn,
      windowHeightIn: windowHeightIn,
      windowWidthIn: windowWidthIn,
      valid: valid,
      color: color
    };
  }

  function hideFitSuggest() {
    if (fitSuggestEl) {
      fitSuggestEl.hidden = true;
    }

    if (fitSuggestTextEl) {
      fitSuggestTextEl.textContent = '';
    }

    suggestedDims = null;
  }

  function hideMatEnlargeNotice() {
    if (matEnlargeEl) {
      matEnlargeEl.hidden = true;
    }

    if (matEnlargeTextEl) {
      matEnlargeTextEl.textContent = '';
    }

    if (matEnlargeBtn) {
      matEnlargeBtn.hidden = false;
    }

    matEnlargeTarget = null;
  }

  function resetDesiredVisible() {
    desiredVisibleH = null;
    desiredVisibleW = null;
  }

  function setDesiredVisible(h, w) {
    if (h > 0 && w > 0) {
      desiredVisibleH = h;
      desiredVisibleW = w;
    }
  }

  function parseDimFromParts(whole, frac) {
    var w = parseFloat(whole);

    if (!w || isNaN(w)) {
      return 0;
    }

    if (!frac || frac === '---') {
      return w;
    }

    var parts = String(frac).split('/');

    if (parts.length === 2) {
      return w + parseFloat(parts[0]) / parseFloat(parts[1]);
    }

    return w;
  }

  function windowMatchesDesired(mat) {
    return (
      desiredVisibleH &&
      desiredVisibleW &&
      approxInches(mat.windowHeightIn, desiredVisibleH) &&
      approxInches(mat.windowWidthIn, desiredVisibleW)
    );
  }

  function windowNeedsEnlargeForMat(mat) {
    if (!desiredVisibleH || !desiredVisibleW) {
      return false;
    }

    return (
      mat.windowHeightIn < desiredVisibleH - 0.13 ||
      mat.windowWidthIn < desiredVisibleW - 0.13
    );
  }

  function windowAspectMatchesPhoto(mat) {
    if (!artworkImage || !artworkImage.width || !mat || !mat.valid) {
      return true;
    }

    var photoRatio = artworkImage.width / artworkImage.height;
    var windowRatio = mat.windowWidthIn / mat.windowHeightIn;

    return Math.abs(photoRatio - windowRatio) / photoRatio < 0.02;
  }

  function isMatSelected() {
    return matCheckbox && matCheckbox.checked;
  }

  function updateMatOptionsVisibility() {
    if (!matOptionsWrap) {
      return;
    }

    var matOn = isMatSelected();
    matOptionsWrap.hidden = !matOn;

    if (!matOn) {
      hideMatEnlargeNotice();
    }
  }

  function updateMatColorSelectStyle() {
    if (!matColorSelect) {
      return;
    }

    var option = matColorSelect.options[matColorSelect.selectedIndex];
    var swatch = option ? option.getAttribute('data-swatch') || matColorSelect.value : '#f5f0e8';

    matColorSelect.style.backgroundColor = swatch;
    matColorSelect.style.color = isDarkHex(swatch) ? '#ffffff' : '#222222';
  }

  // Device-pixel-ratio the renderer draws at. Kept in a module var so the
  // offscreen tiled-band canvas can match the main canvas's resolution and
  // stay crisp on high-DPI screens (otherwise the whole preview is upscaled
  // from CSS pixels and looks soft next to the server-rendered dashboard).
  var RENDER_SCALE = 1;

  // Size the canvas for the current DPR: backing store in device pixels, CSS
  // box in logical pixels, and a context transform so all drawing code can keep
  // using logical coordinates. Also clears the (logical) area.
  function setCanvasSize(ctx, cw, ch) {
    var dpr = Math.max(1, Math.min(window.devicePixelRatio || 1, 3));
    RENDER_SCALE = dpr;
    canvas.width = Math.round(cw * dpr);
    canvas.height = Math.round(ch * dpr);
    canvas.style.width = cw + 'px';
    canvas.style.height = ch + 'px';
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, cw, ch);
  }

  function redraw() {
    if (lastHeight && lastWidth) {
      draw(lastHeight, lastWidth);
    }
  }

  // ---- Click to enlarge --------------------------------------------------
  // The preview canvas moves into a full-screen overlay and is redrawn at that
  // size, so it stays sharp instead of being scaled up. Corner photos open
  // there too.
  var zoomEl = null;
  var zoomBody = null;
  var zoomImg = null;
  var zoomMode = null; // 'canvas' | 'photo' | null
  var canvasHome = null;

  function canvasBox(ratio, maxH) {
    if (zoomMode === 'canvas' && zoomBody) {
      var zw = zoomBody.clientWidth || 800;
      var zh = zoomBody.clientHeight || 600;
      return { cw: zw, ch: Math.min(Math.round(zw * ratio), zh) };
    }
    var cw = container.clientWidth || 400;
    return { cw: cw, ch: Math.min(Math.round(cw * ratio), maxH) };
  }

  function ensureZoom() {
    if (zoomEl) {
      return;
    }
    zoomEl = document.createElement('div');
    zoomEl.className = 'ff-zoom';
    zoomEl.hidden = true;
    zoomEl.setAttribute('role', 'dialog');
    zoomEl.setAttribute('aria-modal', 'true');
    zoomEl.setAttribute('aria-label', 'Enlarged preview');
    zoomEl.innerHTML =
      '<button type="button" class="ff-zoom__close" aria-label="Close">\u00d7</button>' +
      '<div class="ff-zoom__body"><img class="ff-zoom__img" alt=""></div>';
    document.body.appendChild(zoomEl);
    zoomBody = zoomEl.querySelector('.ff-zoom__body');
    zoomImg = zoomEl.querySelector('.ff-zoom__img');
    zoomEl.addEventListener('click', function (e) {
      if (e.target === zoomEl || e.target.closest('.ff-zoom__close')) {
        closeZoom();
      }
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && zoomMode) {
        closeZoom();
      }
    });
  }

  function openZoom(mode, src) {
    ensureZoom();
    zoomMode = mode;
    zoomEl.hidden = false;
    document.documentElement.classList.add('ff-zoom-open');
    if (mode === 'photo') {
      zoomImg.hidden = false;
      zoomImg.src = src;
    } else {
      zoomImg.hidden = true;
      canvasHome = document.createComment('ff-canvas-home');
      canvas.parentNode.insertBefore(canvasHome, canvas);
      zoomBody.appendChild(canvas);
      redraw();
    }
  }

  function closeZoom() {
    if (!zoomMode) {
      return;
    }
    var wasCanvas = zoomMode === 'canvas';
    zoomMode = null;
    zoomEl.hidden = true;
    document.documentElement.classList.remove('ff-zoom-open');
    zoomImg.removeAttribute('src');
    if (wasCanvas && canvasHome) {
      canvasHome.parentNode.insertBefore(canvas, canvasHome);
      canvasHome.parentNode.removeChild(canvasHome);
      canvasHome = null;
      redraw();
    }
  }

  function bindZoom() {
    canvas.classList.add('ff-zoomable');
    canvas.setAttribute('title', 'Click to enlarge');
    canvas.addEventListener('click', function () {
      if (!zoomMode && lastHeight && lastWidth) {
        openZoom('canvas');
      }
    });
    if (productPhotoImg) {
      productPhotoImg.classList.add('ff-zoomable');
      productPhotoImg.setAttribute('title', 'Click to enlarge');
      productPhotoImg.addEventListener('click', function () {
        var src = productPhotoImg.currentSrc || productPhotoImg.src;
        if (src) {
          openZoom('photo', src);
        }
      });
    }
  }

  function clearArtwork() {
    artworkImage = null;

    if (fileInput) {
      fileInput.value = '';
    }

    if (removeBtn) {
      removeBtn.hidden = true;
    }

    hideFitSuggest();
    resetDesiredVisible();
    lastChangedDim = null;

    redraw();
  }

  function setArtworkFromFile(file) {
    if (!file || !file.type.match(/^image\//)) {
      return;
    }

    if (file.size > MAX_UPLOAD_BYTES) {
      alert('Please choose an image under 8 MB.');
      return;
    }

    var reader = new FileReader();

    reader.onload = function (event) {
      var img = new Image();

      img.onload = function () {
        artworkImage = img;

        if (removeBtn) {
          removeBtn.hidden = false;
        }

        // Fresh upload: start from the default-size suggestion, not whichever
        // side was last touched.
        lastChangedDim = null;

        if (lastHeight && lastWidth) {
          setDesiredVisible(lastHeight, lastWidth);
        }

        updateSizeSuggestion();
        redraw();
      };

      img.onerror = function () {
        alert('Could not load that image. Try a JPG or PNG file.');
      };

      img.src = event.target.result;
    };

    reader.readAsDataURL(file);
  }

  function bindArtworkUpload() {
    if (!fileInput) {
      return;
    }

    fileInput.addEventListener('change', function () {
      var file = fileInput.files && fileInput.files[0];

      if (file) {
        setArtworkFromFile(file);
      }
    });

    if (removeBtn) {
      removeBtn.addEventListener('click', clearArtwork);
    }

    if (fitCheckbox) {
      fitCheckbox.addEventListener('change', redraw);
    }

    if (fitApplyBtn) {
      fitApplyBtn.addEventListener('click', applySuggestedDims);
    }
  }

  // Refresh the DPO "green box" note so its mat wording tracks the current
  // matboard controls (which live here, not in the DPO widget).
  function refreshDimensionSummary() {
    if (window.FFDpoDimensions && window.FFDpoDimensions.refresh) {
      window.FFDpoDimensions.refresh();
    }
  }

  function bindMatControls() {
    if (matCheckbox) {
      matCheckbox.addEventListener('change', function () {
        updateMatOptionsVisibility();

        if (matCheckbox.checked) {
          // Artwork mode: the size on screen is the customer's artwork; remember
          // it and grow the ordered frame to fit that art plus the mat border.
          // Opening mode: the entered size is a FIXED opening — leave it be; the
          // mat insets and getMatState shrinks the visible window.
          // Kit add-ons: never write back to DPO — the dropdowns stay artwork size.
          if (usesEnlarge() && lastHeight && lastWidth && !kitOwnsArtworkSize()) {
            matBaseH = lastHeight;
            matBaseW = lastWidth;
            setDesiredVisible(matBaseH, matBaseW);
            growFrameForMat();
          }
        } else {
          // Artwork mode: revert the ordered frame to the artwork size it started
          // from. Opening mode never grew, so there's nothing to revert.
          if (usesEnlarge() && matBaseH && matBaseW && !kitOwnsArtworkSize()) {
            setOrderedFrameInches(matBaseH, matBaseW);
          }
          matBaseH = null;
          matBaseW = null;
          resetDesiredVisible();
        }

        redraw();
        refreshDimensionSummary();
      });
    }

    if (matBorderSelect) {
      matBorderSelect.addEventListener('change', function () {
        // Artwork mode: keep the artwork fixed; re-grow the frame for the new
        // border width. Opening mode: the opening is fixed, so a wider border
        // just eats more of the window — nothing to re-grow.
        if (usesEnlarge() && isMatSelected() && matBaseH && matBaseW && !kitOwnsArtworkSize()) {
          growFrameForMat();
        }

        redraw();
        refreshDimensionSummary();
      });
    }

    if (matOverlapCheckbox) {
      matOverlapCheckbox.addEventListener('change', function () {
        redraw();
        refreshDimensionSummary();
      });
    }

    if (matEnlargeBtn) {
      matEnlargeBtn.addEventListener('click', applyMatEnlarge);
    }

    if (matColorSelect) {
      matColorSelect.addEventListener('change', function () {
        updateMatColorSelectStyle();
        redraw();
        refreshDimensionSummary();
      });
      updateMatColorSelectStyle();
    }

    updateMatOptionsVisibility();
  }

  function parseMouldingWidth(raw) {
    if (!raw) {
      return MOULDING_WIDTH_IN;
    }

    // The width metafield may arrive as a fraction ("5/8\""), a mixed number
    // ("2-1/8\"" or "2 1/8\""), or a plain decimal ("0.62", "1.0\""). Parse all
    // three; a naive "first number" grab reads "5/8" as 5 inches.
    var s = String(raw).trim();
    var value = NaN;

    var mixed = s.match(/(\d+)\s*[-\s]\s*(\d+)\s*\/\s*(\d+)/);
    var frac = s.match(/^\s*(\d+)\s*\/\s*(\d+)/);

    if (mixed) {
      value = parseFloat(mixed[1]) + parseFloat(mixed[2]) / parseFloat(mixed[3]);
    } else if (frac) {
      value = parseFloat(frac[1]) / parseFloat(frac[2]);
    } else {
      var dec = s.match(/[\d.]+/);
      if (dec) {
        value = parseFloat(dec[0]);
      }
    }

    return value > 0 && isFinite(value) ? value : MOULDING_WIDTH_IN;
  }

  function loadMouldingTexture(url) {
    if (!url) {
      return;
    }

    if (mouldingTextureUrl === url && mouldingTextureImage) {
      return;
    }

    mouldingTextureUrl = url;

    // We never read the texture's pixels back (no getImageData/toDataURL), so a
    // "tainted" canvas is fine. We therefore load WITHOUT crossOrigin: the R2
    // public URL doesn't send an Access-Control-Allow-Origin header, and setting
    // crossOrigin='anonymous' would make the load fail and fall back to the
    // procedural (wrong) frame. If pixel export is ever needed, enable CORS on
    // the bucket and restore crossOrigin instead.
    mouldingTextureImage = new Image();

    mouldingTextureImage.onload = function () {
      rotatedStripCanvas = null; // rebuild lazily for the new texture
      redraw();
    };

    mouldingTextureImage.onerror = function () {
      mouldingTextureImage = null;
    };

    mouldingTextureImage.src = url;
  }

  function loadCanvasTexture(url) {
    if (!url) {
      return;
    }
    var img = new Image();
    img.onload = function () {
      canvasTextureImage = img;
      redraw();
    };
    img.onerror = function () {
      canvasTextureImage = null;
    };
    img.src = url;
  }

  function textureRegion(img, region) {
    return {
      sx: img.width * region.x,
      sy: img.height * region.y,
      sw: img.width * region.w,
      sh: img.height * region.h
    };
  }

  function drawImageRegion(ctx, img, region, dx, dy, dw, dh, flipH, flipV) {
    if (dw <= 0 || dh <= 0) {
      return;
    }

    var part = textureRegion(img, region);

    ctx.save();
    ctx.translate(dx + dw / 2, dy + dh / 2);
    ctx.scale(flipH ? -1 : 1, flipV ? -1 : 1);
    ctx.drawImage(img, part.sx, part.sy, part.sw, part.sh, -dw / 2, -dh / 2, dw, dh);
    ctx.restore();
  }

  function drawProceduralBlackBody(ctx, x, y, w, h, horizontal) {
    var grad;

    if (horizontal) {
      grad = ctx.createLinearGradient(x, y, x, y + h);
    } else {
      grad = ctx.createLinearGradient(x, y, x + w, y);
    }

    grad.addColorStop(0, '#2a2624');
    grad.addColorStop(0.12, '#171717');
    grad.addColorStop(0.5, '#101010');
    grad.addColorStop(0.88, '#181818');
    grad.addColorStop(1, '#252525');

    ctx.fillStyle = grad;
    ctx.fillRect(x, y, w, h);
  }

  function drawProceduralGoldLip(ctx, x, y, w, h, horizontal) {
    var grad;

    if (horizontal) {
      grad = ctx.createLinearGradient(x, y, x, y + h);
    } else {
      grad = ctx.createLinearGradient(x, y, x + w, y);
    }

    grad.addColorStop(0, '#d4b06a');
    grad.addColorStop(0.35, '#b8893f');
    grad.addColorStop(0.7, '#8f6a2c');
    grad.addColorStop(1, '#c49a52');

    ctx.fillStyle = grad;
    ctx.fillRect(x, y, w, h);

    ctx.fillStyle = 'rgba(255, 235, 170, 0.14)';
    ctx.fillRect(x, y, w, h);
  }

  function drawRidgeDetail(ctx, x, y, w, h, horizontal, outerEdge) {
    var ridgeCount = 3;
    var ridgeSpan = horizontal ? h : w;
    var start = ridgeSpan * 0.12;
    var end = ridgeSpan * 0.42;
    var step = (end - start) / (ridgeCount - 1);

    ctx.save();
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.08)';
    ctx.lineWidth = 1;

    for (var i = 0; i < ridgeCount; i++) {
      var offset = start + step * i;

      ctx.beginPath();

      if (horizontal) {
        var lineY = outerEdge === 'top' ? y + offset : y + h - offset;
        ctx.moveTo(x, lineY);
        ctx.lineTo(x + w, lineY);
      } else {
        var lineX = outerEdge === 'left' ? x + offset : x + w - offset;
        ctx.moveTo(lineX, y);
        ctx.lineTo(lineX, y + h);
      }

      ctx.stroke();
    }

    ctx.strokeStyle = 'rgba(0, 0, 0, 0.35)';
    ctx.beginPath();

    if (horizontal) {
      var shadowY = outerEdge === 'top' ? y + ridgeSpan * 0.48 : y + h - ridgeSpan * 0.48;
      ctx.moveTo(x, shadowY);
      ctx.lineTo(x + w, shadowY);
    } else {
      var shadowX = outerEdge === 'left' ? x + ridgeSpan * 0.48 : x + w - ridgeSpan * 0.48;
      ctx.moveTo(shadowX, y);
      ctx.lineTo(shadowX, y + h);
    }

    ctx.stroke();
    ctx.restore();
  }

  function drawOuterMahoganyEdge(ctx, x, y, w, h, edge) {
    ctx.save();
    ctx.fillStyle = '#4a1f18';

    if (edge === 'top') {
      ctx.fillRect(x, y, w, Math.max(1, h * 0.04));
    } else if (edge === 'bottom') {
      ctx.fillRect(x, y + h - Math.max(1, h * 0.04), w, Math.max(1, h * 0.04));
    } else if (edge === 'left') {
      ctx.fillRect(x, y, Math.max(1, w * 0.04), h);
    } else {
      ctx.fillRect(x + w - Math.max(1, w * 0.04), y, Math.max(1, w * 0.04), h);
    }

    ctx.restore();
  }

  function drawProceduralStrip(ctx, x, y, w, h, edge) {
    var goldSize = (edge === 'top' || edge === 'bottom' ? h : w) * GOLD_LIP_RATIO;

    if (edge === 'top') {
      var blackH = h - goldSize;
      drawProceduralBlackBody(ctx, x, y, w, blackH, true);
      drawProceduralGoldLip(ctx, x, y + blackH, w, goldSize, true);
      drawRidgeDetail(ctx, x, y, w, blackH, true, 'top');
      drawOuterMahoganyEdge(ctx, x, y, w, h, 'top');
    } else if (edge === 'bottom') {
      blackH = h - goldSize;
      drawProceduralGoldLip(ctx, x, y, w, goldSize, true);
      drawProceduralBlackBody(ctx, x, y + goldSize, w, blackH, true);
      drawRidgeDetail(ctx, x, y + goldSize, w, blackH, true, 'bottom');
      drawOuterMahoganyEdge(ctx, x, y, w, h, 'bottom');
    } else if (edge === 'left') {
      var blackW = w - goldSize;
      drawProceduralBlackBody(ctx, x, y, blackW, h, false);
      drawProceduralGoldLip(ctx, x + blackW, y, goldSize, h, false);
      drawRidgeDetail(ctx, x, y, blackW, h, false, 'left');
      drawOuterMahoganyEdge(ctx, x, y, w, h, 'left');
    } else {
      blackW = w - goldSize;
      drawProceduralGoldLip(ctx, x, y, goldSize, h, false);
      drawProceduralBlackBody(ctx, x + goldSize, y, blackW, h, false);
      drawRidgeDetail(ctx, x + goldSize, y, blackW, h, false, 'right');
      drawOuterMahoganyEdge(ctx, x, y, w, h, 'right');
    }
  }

  // Clip the context to one side's 45deg miter trapezoid (outer edge full length,
  // inner edge inset by the moulding width, ends cut at 45deg).
  function clipSideTrapezoid(ctx, edge, ox, oy, outerW, outerH, m) {
    ctx.beginPath();

    if (edge === 'top') {
      ctx.moveTo(ox, oy);
      ctx.lineTo(ox + outerW, oy);
      ctx.lineTo(ox + outerW - m, oy + m);
      ctx.lineTo(ox + m, oy + m);
    } else if (edge === 'bottom') {
      ctx.moveTo(ox, oy + outerH);
      ctx.lineTo(ox + outerW, oy + outerH);
      ctx.lineTo(ox + outerW - m, oy + outerH - m);
      ctx.lineTo(ox + m, oy + outerH - m);
    } else if (edge === 'left') {
      ctx.moveTo(ox, oy);
      ctx.lineTo(ox, oy + outerH);
      ctx.lineTo(ox + m, oy + outerH - m);
      ctx.lineTo(ox + m, oy + m);
    } else {
      ctx.moveTo(ox + outerW, oy);
      ctx.lineTo(ox + outerW, oy + outerH);
      ctx.lineTo(ox + outerW - m, oy + outerH - m);
      ctx.lineTo(ox + outerW - m, oy + m);
    }

    ctx.closePath();
    ctx.clip();
  }

  // Build (and cache) a 90deg-rotated copy of the cross-section strip so the
  // vertical arms (left/right) map the face across their horizontal thickness.
  function getRotatedStrip(img) {
    if (rotatedStripCanvas) {
      return rotatedStripCanvas;
    }

    var c = document.createElement('canvas');
    c.width = img.naturalHeight;
    c.height = img.naturalWidth;
    var cctx = c.getContext('2d');
    cctx.translate(c.width / 2, c.height / 2);
    cctx.rotate(Math.PI / 2);
    cctx.drawImage(img, -img.naturalWidth / 2, -img.naturalHeight / 2);
    rotatedStripCanvas = c;
    return c;
  }

  var WHOLE_REGION = { x: 0, y: 0, w: 1, h: 1 };

  // Build a horizontal moulding band (lengthPx x thicknessPx) by TILING the
  // rectified cross-section strip along its length, matching the dashboard
  // renderer. The strip is scaled so its face (short) axis equals the band
  // thickness, preserving the strip's true length:face aspect — so each tile
  // reproduces the real pattern density and a longer side simply gets more
  // repeats instead of stretching one photo. When `mirror` is true, every other
  // tile is flipped so adjacent seams reflect and hide; a directional moulding
  // (NO_MIRROR) tiles straight.
  function makeTiledBandCanvas(img, lengthPx, thicknessPx, mirror) {
    // Build at the current device-pixel-ratio so the band is 1:1 with the
    // (DPR-scaled) main canvas when drawn back at logical size — no upscaling.
    var s = RENDER_SCALE || 1;
    var c = document.createElement('canvas');
    c.width = Math.max(2, Math.round(lengthPx * s));
    c.height = Math.max(2, Math.round(thicknessPx * s));
    var cx = c.getContext('2d');
    cx.imageSmoothingEnabled = true;
    cx.imageSmoothingQuality = 'high';
    var srcW = img.naturalWidth || img.width;
    var srcH = img.naturalHeight || img.height;
    var tileLen = Math.max(2, Math.round(srcW * (c.height / Math.max(1, srcH))));
    var i = 0;
    for (var x = 0; x < c.width; x += tileLen) {
      var flip = mirror && i % 2 === 1;
      cx.save();
      cx.translate(x + tileLen / 2, c.height / 2);
      cx.scale(flip ? -1 : 1, 1);
      cx.drawImage(img, -tileLen / 2, -c.height / 2, tileLen, c.height);
      cx.restore();
      i++;
    }
    return c;
  }

  // Strip-mode side draw: TILE the cross-section along each side (top/bottom
  // horizontal; left/right reuse a horizontal band rotated into place). The
  // strip's outer edge stays on the frame's outer edge on every side.
  function drawStripSide(ctx, img, edge, ox, oy, outerW, outerH, m) {
    var mirror = !NO_MIRROR;

    if (edge === 'top' || edge === 'bottom') {
      var band = makeTiledBandCanvas(img, outerW, m, mirror);
      ctx.save();
      if (edge === 'top') {
        ctx.drawImage(band, ox, oy, outerW, m);
      } else {
        ctx.translate(ox, oy + outerH);
        ctx.scale(1, -1); // outer edge to the bottom
        ctx.drawImage(band, 0, 0, outerW, m);
      }
      ctx.restore();
      return;
    }

    // Vertical sides: tile a horizontal band of length=outerH, then rotate it.
    var vband = makeTiledBandCanvas(img, outerH, m, mirror);
    ctx.save();
    if (edge === 'left') {
      ctx.translate(ox, oy + outerH);
      ctx.rotate(-Math.PI / 2); // outer edge to the left
      ctx.drawImage(vband, 0, 0, outerH, m);
    } else {
      ctx.translate(ox + outerW, oy);
      ctx.rotate(Math.PI / 2); // outer edge to the right
      ctx.drawImage(vband, 0, 0, outerH, m);
    }
    ctx.restore();
  }

  // Neutral "representative" moulding for products without a real stick photo:
  // a soft gray bevel that still reacts to size, so the customer sees the shape
  // and proportions without us implying a specific (e.g. black) finish.
  function drawGrayStrip(ctx, x, y, w, h, edge) {
    var horizontal = edge === 'top' || edge === 'bottom';
    var grad = horizontal
      ? ctx.createLinearGradient(x, y, x, y + h)
      : ctx.createLinearGradient(x, y, x + w, y);
    var outerFirst = edge === 'top' || edge === 'left';
    grad.addColorStop(0, outerFirst ? '#d8d8d8' : '#bdbdbd');
    grad.addColorStop(0.5, '#cccccc');
    grad.addColorStop(1, outerFirst ? '#bdbdbd' : '#d8d8d8');
    ctx.fillStyle = grad;
    ctx.fillRect(x, y, w, h);

    // Thin inner-lip line for a hint of depth.
    ctx.strokeStyle = 'rgba(0, 0, 0, 0.12)';
    ctx.lineWidth = 1;
    ctx.beginPath();
    if (edge === 'top') {
      ctx.moveTo(x, y + h - 0.5);
      ctx.lineTo(x + w, y + h - 0.5);
    } else if (edge === 'bottom') {
      ctx.moveTo(x, y + 0.5);
      ctx.lineTo(x + w, y + 0.5);
    } else if (edge === 'left') {
      ctx.moveTo(x + w - 0.5, y);
      ctx.lineTo(x + w - 0.5, y + h);
    } else {
      ctx.moveTo(x + 0.5, y);
      ctx.lineTo(x + 0.5, y + h);
    }
    ctx.stroke();
  }

  function drawSide(ctx, img, edge, ox, oy, outerW, outerH, m) {
    var hasTexture = img && img.complete && img.naturalWidth > 0;

    // In strip mode a real moulding photo is expected. While it's still loading
    // (or failed), show the neutral gray placeholder rather than flashing the
    // legacy procedural "sample" frame.
    if ((MISSING_TEXTURE || STRIP_MODE) && !hasTexture) {
      ctx.save();
      if (edge === 'left' || edge === 'right') {
        clipSideTrapezoid(ctx, edge, ox, oy, outerW, outerH, m);
      }
      if (edge === 'top') {
        drawGrayStrip(ctx, ox, oy, outerW, m, 'top');
      } else if (edge === 'bottom') {
        drawGrayStrip(ctx, ox, oy + outerH - m, outerW, m, 'bottom');
      } else if (edge === 'left') {
        drawGrayStrip(ctx, ox, oy, m, outerH, 'left');
      } else {
        drawGrayStrip(ctx, ox + outerW - m, oy, m, outerH, 'right');
      }
      ctx.restore();
      return;
    }

    if (STRIP_MODE && hasTexture) {
      ctx.save();
      if (edge === 'left' || edge === 'right') {
        clipSideTrapezoid(ctx, edge, ox, oy, outerW, outerH, m);
      }
      drawStripSide(ctx, img, edge, ox, oy, outerW, outerH, m);
      ctx.restore();
      return;
    }

    ctx.save();

    // Top/bottom are drawn as full rectangles underneath; left/right are mitered
    // on top. This keeps a visible miter diagonal but avoids the anti-aliased
    // background sliver (the "white line") that appears when all four sides are
    // clipped trapezoids meeting at a point.
    if (edge === 'left' || edge === 'right') {
      clipSideTrapezoid(ctx, edge, ox, oy, outerW, outerH, m);
    }

    if (edge === 'top') {
      if (hasTexture) {
        drawImageRegion(ctx, img, MOULDING_SRC.topSlice, ox, oy, outerW, m, false, false);
      } else {
        drawProceduralStrip(ctx, ox, oy, outerW, m, 'top');
      }
    } else if (edge === 'bottom') {
      if (hasTexture) {
        drawImageRegion(ctx, img, MOULDING_SRC.topSlice, ox, oy + outerH - m, outerW, m, false, true);
      } else {
        drawProceduralStrip(ctx, ox, oy + outerH - m, outerW, m, 'bottom');
      }
    } else if (edge === 'left') {
      if (hasTexture) {
        drawImageRegion(ctx, img, MOULDING_SRC.leftSlice, ox, oy, m, outerH, false, false);
      } else {
        drawProceduralStrip(ctx, ox, oy, m, outerH, 'left');
      }
    } else {
      if (hasTexture) {
        drawImageRegion(ctx, img, MOULDING_SRC.leftSlice, ox + outerW - m, oy, m, outerH, true, false);
      } else {
        drawProceduralStrip(ctx, ox + outerW - m, oy, m, outerH, 'right');
      }
    }

    ctx.restore();
  }

  function drawMouldingFrame(ctx, ox, oy, outerW, outerH, mouldingPx) {
    var img = mouldingTextureImage;

    drawSide(ctx, img, 'top', ox, oy, outerW, outerH, mouldingPx);
    drawSide(ctx, img, 'bottom', ox, oy, outerW, outerH, mouldingPx);
    drawSide(ctx, img, 'left', ox, oy, outerW, outerH, mouldingPx);
    drawSide(ctx, img, 'right', ox, oy, outerW, outerH, mouldingPx);
  }

  // Floater flat shots capture the WHOLE profile: the visible outer rim PLUS the
  // deep tray/foot that sits hidden under the canvas. `data-moulding-width` is
  // that full profile width; `data-floater-face` is only the rim you actually
  // see head-on. Cropping the strip to the outer rim fraction (and caching it)
  // lets us tile just the face at true scale instead of squashing the entire
  // profile into the ~1/2" rim band. The strip's row 0 is the frame's outer edge.
  var _floaterFaceCache = null;
  function getFloaterFaceTexture() {
    var img = mouldingTextureImage;
    if (!img || !img.complete || !img.naturalWidth) {
      return img;
    }
    var full = MOULDING_WIDTH_IN;
    var face = FLOATER_FACE_IN;
    // Only crop when the strip is meaningfully wider than the visible rim.
    if (!(full > 0 && face > 0 && face / full < 0.9)) {
      return img;
    }
    var frac = Math.max(0.12, face / full);
    if (
      _floaterFaceCache &&
      _floaterFaceCache.src === img.src &&
      Math.abs(_floaterFaceCache.frac - frac) < 0.001
    ) {
      return _floaterFaceCache.canvas;
    }
    var sw = img.naturalWidth;
    var sh = img.naturalHeight;
    var cropH = Math.max(2, Math.round(sh * frac));
    var c = document.createElement('canvas');
    c.width = sw;
    c.height = cropH;
    c.getContext('2d').drawImage(img, 0, 0, sw, cropH, 0, 0, sw, cropH);
    _floaterFaceCache = { src: img.src, frac: frac, canvas: c };
    return c;
  }

  // Draw the four floater rim sides using the cropped face texture so only the
  // real rim shows (the tray foot stays hidden behind the canvas).
  function drawFloaterWall(ctx, ox, oy, outerW, outerH, facePx) {
    var faceImg = getFloaterFaceTexture();
    var edges = ['top', 'bottom', 'left', 'right'];
    for (var i = 0; i < edges.length; i++) {
      var edge = edges[i];
      ctx.save();
      if (edge === 'left' || edge === 'right') {
        clipSideTrapezoid(ctx, edge, ox, oy, outerW, outerH, facePx);
      }
      drawStripSide(ctx, faceImg, edge, ox, oy, outerW, outerH, facePx);
      ctx.restore();
    }
  }

  function drawMatWithWindow(ctx, x, y, w, h, windowX, windowY, windowW, windowH, matColorHex) {
    ctx.fillStyle = matColorHex;
    ctx.fillRect(x, y, w, h);
    ctx.fillStyle = ARTWORK_WINDOW_FILL;
    ctx.fillRect(windowX, windowY, windowW, windowH);
  }

  function isFitMode() {
    // Default to "fit whole image" (contain). Only crop-to-fill when the user
    // explicitly unchecks the toggle.
    return !fitCheckbox || fitCheckbox.checked;
  }

  var DEFAULT_SUGGEST_LONG = 16;

  var EIGHTH_LABELS = {
    0: '',
    1: '1/8',
    2: '1/4',
    3: '3/8',
    4: '1/2',
    5: '5/8',
    6: '3/4',
    7: '7/8'
  };

  function inchesToWholeFraction(value) {
    value = Math.max(5, Math.min(60, value));

    var eighths = Math.round(value * 8);
    var whole = Math.floor(eighths / 8);
    var rem = eighths - whole * 8;

    if (whole < 5) {
      whole = 5;
      rem = 0;
    }

    if (whole > 60) {
      whole = 60;
      rem = 0;
    }

    return { whole: String(whole), frac: EIGHTH_LABELS[rem] };
  }

  function wholeFractionLabel(wf) {
    return wf.frac ? wf.whole + '-' + wf.frac + '"' : wf.whole + '"';
  }

  function currentBorderIn() {
    if (!matCheckbox || !matCheckbox.checked || !matBorderSelect) {
      return 0;
    }

    var b = parseFloat(matBorderSelect.value);
    return isNaN(b) || b < 0 ? 0 : b;
  }

  // Given one anchored frame dimension, compute both frame dimensions so the mat
  // window matches the photo ratio, keeping both sides within 5"-60".
  function suggestFromAnchor(anchorDim, anchorFrameValue, ratio, b) {
    var anchorWin = anchorFrameValue - b * 2;
    if (anchorWin < 1) {
      b = 0;
      anchorWin = anchorFrameValue;
    }

    // ratio = width / height
    var otherWin = anchorDim === 'height' ? anchorWin * ratio : anchorWin / ratio;
    var otherFrame = otherWin + b * 2;

    // If the computed side falls outside 5"-60", clamp it and re-derive the
    // anchored side from the clamp so the ratio (no white space) is preserved.
    if (otherFrame < 5 || otherFrame > 60) {
      otherFrame = Math.max(5, Math.min(60, otherFrame));
      otherWin = otherFrame - b * 2;
      anchorWin = anchorDim === 'height' ? otherWin / ratio : otherWin * ratio;
      anchorFrameValue = anchorWin + b * 2;
    }

    anchorFrameValue = Math.max(5, Math.min(60, anchorFrameValue));

    if (anchorDim === 'height') {
      return { frameH: anchorFrameValue, frameW: otherFrame };
    }

    return { frameH: otherFrame, frameW: anchorFrameValue };
  }

  function updateSizeSuggestion() {
    // The "recommended sizes" callout has been retired in favor of the simpler
    // live summary + mat toggle. Keep the function so existing callers are safe,
    // but never surface a size recommendation.
    hideFitSuggest();
    return;

    /* eslint-disable no-unreachable */
    if (!fitSuggestEl || !fitSuggestTextEl) {
      return;
    }

    if (!artworkImage || !artworkImage.width || !artworkImage.height) {
      hideFitSuggest();
      return;
    }

    var ratio = artworkImage.width / artworkImage.height; // width / height
    var isLandscape = ratio >= 1;
    var b = currentBorderIn();
    var matState = isMatSelected() ? getMatState(lastHeight, lastWidth) : null;

    // With matboard: enlarge handles "don't crop my image"; fit handles aspect ratio
    // in the mat window once the visible area is large enough. Never show both.
    if (matState && matState.enabled && matState.valid) {
      if (windowNeedsEnlargeForMat(matState)) {
        hideFitSuggest();
        return;
      }

      if (windowAspectMatchesPhoto(matState)) {
        hideFitSuggest();
        return;
      }
    }

    // Decide which dimension to anchor on:
    //  - if the customer just changed Height or Width, anchor on THAT side and
    //    compute the other to match the photo (so either field drives it).
    //  - otherwise (fresh upload), anchor the photo's long side to the largest
    //    chosen dimension, or a sensible default size.
    var anchorDim;
    var anchorVal;

    if (lastChangedDim === 'height' && lastHeight) {
      anchorDim = 'height';
      anchorVal = lastHeight;
    } else if (lastChangedDim === 'width' && lastWidth) {
      anchorDim = 'width';
      anchorVal = lastWidth;
    } else {
      var curLong = Math.max(lastHeight || 0, lastWidth || 0);
      anchorVal = curLong > 5 ? curLong : DEFAULT_SUGGEST_LONG;
      anchorDim = isLandscape ? 'width' : 'height';
    }

    anchorVal = Math.max(5, Math.min(60, anchorVal));

    var dims = suggestFromAnchor(anchorDim, anchorVal, ratio, b);
    var h = inchesToWholeFraction(dims.frameH);
    var w = inchesToWholeFraction(dims.frameW);

    suggestedDims = {
      hWhole: h.whole,
      hFrac: h.frac,
      wWhole: w.whole,
      wFrac: w.frac
    };

    // If the suggested size is already what's entered, there's nothing to fix
    // (no white space) -- don't show a redundant recommendation. This also keeps
    // the box from re-appearing right after the "Enlarge frame for mat" action.
    if (
      lastHeight &&
      lastWidth &&
      approxInches(dims.frameH, lastHeight) &&
      approxInches(dims.frameW, lastWidth)
    ) {
      hideFitSuggest();
      return;
    }

    var matNote = b > 0 ? ' (frame opening, mat included)' : '';

    fitSuggestTextEl.textContent =
      'Your photo fits with no white space at ' +
      wholeFractionLabel(w) +
      ' \u00d7 ' +
      wholeFractionLabel(h) +
      matNote +
      '.';

    fitSuggestEl.hidden = false;
  }

  function applySuggestedDims() {
    if (!suggestedDims || !window.FFDpoDimensions || !window.FFDpoDimensions.setDimensions) {
      return;
    }

    skipProgrammaticDimUpdate = true;

    var frameH = parseDimFromParts(suggestedDims.hWhole, suggestedDims.hFrac);
    var frameW = parseDimFromParts(suggestedDims.wWhole, suggestedDims.wFrac);
    var border = isMatSelected() ? currentBorderIn() : 0;

    if (border > 0) {
      setDesiredVisible(frameH - border * 2, frameW - border * 2);
    } else {
      setDesiredVisible(frameH, frameW);
    }

    window.FFDpoDimensions.setDimensions(
      suggestedDims.hWhole,
      suggestedDims.hFrac,
      suggestedDims.wWhole,
      suggestedDims.wFrac
    );
  }

  function approxInches(a, b) {
    return Math.abs(a - b) < 0.13; // within ~1/8"
  }

  function updateMatEnlargeNotice() {
    // The frame now grows automatically to fit the artwork + mat, so there's no
    // manual "enlarge" step. Keep the element hidden; the size adjustment is
    // stated in the plain-language summary and the DPO note instead.
    hideMatEnlargeNotice();
  }

  function applyMatEnlarge() {
    if (!matEnlargeTarget || !window.FFDpoDimensions || !window.FFDpoDimensions.setDimensions) {
      return;
    }

    // desiredVisible stays fixed — enlarge grows the frame so the mat window
    // matches that target instead of treating each new opening as a new target.
    skipProgrammaticDimUpdate = true;

    window.FFDpoDimensions.setDimensions(
      matEnlargeTarget.hWhole,
      matEnlargeTarget.hFrac,
      matEnlargeTarget.wWhole,
      matEnlargeTarget.wFrac
    );
  }

  // Push a new ordered frame size to the DPO (which recalculates price). Guarded
  // so the resulting change event doesn't get treated as a manual customer edit.
  function setOrderedFrameInches(hIn, wIn) {
    if (!window.FFDpoDimensions || !window.FFDpoDimensions.setDimensions) {
      return;
    }

    var nh = inchesToWholeFraction(hIn);
    var nw = inchesToWholeFraction(wIn);
    skipProgrammaticDimUpdate = true;
    window.FFDpoDimensions.setDimensions(nh.whole, nh.frac, nw.whole, nw.frac);
  }

  // Grow the ordered frame so its opening fits the captured artwork size plus
  // the mat border on each side (keeps the customer's full art visible).
  function growFrameForMat() {
    if (kitOwnsArtworkSize()) {
      return;
    }
    var b = currentBorderIn();

    if (!isMatSelected() || b <= 0 || !matBaseH || !matBaseW) {
      return;
    }

    setOrderedFrameInches(matBaseH + b * 2, matBaseW + b * 2);
  }

  function drawArtwork(ctx, x, y, w, h) {
    if (!artworkImage || !artworkImage.width || w <= 0 || h <= 0) {
      return false;
    }

    ctx.save();
    ctx.beginPath();
    ctx.rect(x, y, w, h);
    ctx.clip();

    // Always fill the opening (cover). A photo whose proportions differ from the
    // frame is cropped a touch rather than letterboxed with confusing white
    // gaps — the customer entered their artwork size, so the picture fills it.
    var scale = Math.max(w / artworkImage.width, h / artworkImage.height);
    var drawW = artworkImage.width * scale;
    var drawH = artworkImage.height * scale;
    var drawX = x + (w - drawW) / 2;
    var drawY = y + (h - drawH) / 2;

    ctx.drawImage(artworkImage, drawX, drawY, drawW, drawH);

    // This upload is only a size/frame preview — we don't print or sell prints,
    // so mark the customer's image so it's never mistaken for a proof.
    drawPreviewWatermark(ctx, x, y, w, h);

    ctx.restore();

    return true;
  }

  function drawPreviewWatermark(ctx, x, y, w, h) {
    ctx.save();
    ctx.beginPath();
    ctx.rect(x, y, w, h);
    ctx.clip();

    var fontPx = Math.max(13, Math.round(Math.min(w, h) * 0.1));
    ctx.font = '700 ' + fontPx + 'px system-ui, -apple-system, "Segoe UI", sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.translate(x + w / 2, y + h / 2);
    ctx.rotate(-Math.PI / 6);

    var text = 'PREVIEW ONLY';
    var stepX = ctx.measureText(text).width + fontPx * 1.8;
    var stepY = fontPx * 2.8;
    var reach = Math.sqrt(w * w + h * h);
    ctx.fillStyle = 'rgba(255, 255, 255, 0.30)';
    ctx.strokeStyle = 'rgba(0, 0, 0, 0.16)';
    ctx.lineWidth = Math.max(1, fontPx * 0.03);

    var row = 0;
    for (var yy = -reach; yy < reach; yy += stepY) {
      var offset = (row % 2) * (stepX / 2);
      for (var xx = -reach + offset; xx < reach; xx += stepX) {
        ctx.strokeText(text, xx, yy);
        ctx.fillText(text, xx, yy);
      }
      row += 1;
    }

    ctx.restore();
  }

  function drawDimensionLabel(ctx, text, x, y, rotate, lightOnDark) {
    ctx.save();
    ctx.font = '600 12px system-ui, -apple-system, "Segoe UI", sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.translate(x, y);

    if (rotate) {
      ctx.rotate(-Math.PI / 2);
    }

    if (lightOnDark) {
      ctx.shadowColor = 'rgba(0, 0, 0, 0.55)';
      ctx.shadowBlur = 4;
      ctx.fillStyle = '#f8f8f8';
    } else {
      ctx.fillStyle = '#3d4a3d';
    }

    ctx.fillText(text, 0, 0);
    ctx.restore();
  }

  function drawCenterLabels(ctx, mainText, subText, x, y, lightOnDark) {
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';

    if (lightOnDark) {
      ctx.shadowColor = 'rgba(0, 0, 0, 0.55)';
      ctx.shadowBlur = 4;
      ctx.fillStyle = '#f5f5f5';
    } else {
      ctx.shadowColor = 'transparent';
      ctx.shadowBlur = 0;
      ctx.fillStyle = '#3d4a3d';
    }

    ctx.font = '600 13px system-ui, -apple-system, "Segoe UI", sans-serif';
    ctx.fillText(mainText, x, y - 6);

    ctx.font = '500 11px system-ui, -apple-system, "Segoe UI", sans-serif';
    ctx.fillStyle = lightOnDark ? '#e8e8e8' : '#5c6b5c';
    ctx.fillText(subText, x, y + 14);
  }

  var DIM_LINE_COLOR = 'rgba(90, 90, 90, 0.9)';
  var DIM_TEXT_COLOR = '#3d4a3d';

  function drawArrowHead(ctx, x, y, dir) {
    // dir: 'left' | 'right' | 'up' | 'down' — arrow points that way.
    var s = 4;
    ctx.beginPath();
    if (dir === 'left') {
      ctx.moveTo(x, y);
      ctx.lineTo(x + s, y - s);
      ctx.lineTo(x + s, y + s);
    } else if (dir === 'right') {
      ctx.moveTo(x, y);
      ctx.lineTo(x - s, y - s);
      ctx.lineTo(x - s, y + s);
    } else if (dir === 'up') {
      ctx.moveTo(x, y);
      ctx.lineTo(x - s, y + s);
      ctx.lineTo(x + s, y + s);
    } else {
      ctx.moveTo(x, y);
      ctx.lineTo(x - s, y - s);
      ctx.lineTo(x + s, y - s);
    }
    ctx.closePath();
    ctx.fill();
  }

  // Draw a small dark label on a soft light pad so it stays readable wherever it
  // sits (over the light margin, or breaking a dimension line).
  function drawPaddedLabel(ctx, text, x, y, rotate) {
    ctx.save();
    ctx.font = '600 12px system-ui, -apple-system, "Segoe UI", sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    var w = ctx.measureText(text).width;
    var padX = 4;
    var padY = 3;
    ctx.translate(x, y);
    if (rotate) {
      ctx.rotate(-Math.PI / 2);
    }
    ctx.fillStyle = 'rgba(247, 247, 247, 0.92)';
    ctx.fillRect(-w / 2 - padX, -8 - padY + 2, w + padX * 2, 16 + padY);
    ctx.fillStyle = DIM_TEXT_COLOR;
    ctx.fillText(text, 0, 1);
    ctx.restore();
  }

  // Horizontal architectural dimension: extension lines run from the two edge
  // x-positions (at yEdge) out to the dimension line at yDim, which carries the
  // measurement between arrowheads. Used above the frame (opening/outside width).
  function drawDimLineH(ctx, x1, x2, yEdge, yDim, label) {
    if (x2 - x1 < 8) {
      return;
    }
    ctx.save();
    ctx.strokeStyle = DIM_LINE_COLOR;
    ctx.fillStyle = DIM_LINE_COLOR;
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(x1, yEdge);
    ctx.lineTo(x1, yDim);
    ctx.moveTo(x2, yEdge);
    ctx.lineTo(x2, yDim);
    ctx.moveTo(x1, yDim);
    ctx.lineTo(x2, yDim);
    ctx.stroke();
    drawArrowHead(ctx, x1, yDim, 'left');
    drawArrowHead(ctx, x2, yDim, 'right');
    ctx.restore();
    if (label) {
      drawPaddedLabel(ctx, label, (x1 + x2) / 2, yDim, false);
    }
  }

  // Vertical architectural dimension (opening/outside height), drawn left of the
  // frame with a rotated label.
  function drawDimLineV(ctx, y1, y2, xEdge, xDim, label) {
    if (y2 - y1 < 8) {
      return;
    }
    ctx.save();
    ctx.strokeStyle = DIM_LINE_COLOR;
    ctx.fillStyle = DIM_LINE_COLOR;
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(xEdge, y1);
    ctx.lineTo(xDim, y1);
    ctx.moveTo(xEdge, y2);
    ctx.lineTo(xDim, y2);
    ctx.moveTo(xDim, y1);
    ctx.lineTo(xDim, y2);
    ctx.stroke();
    drawArrowHead(ctx, xDim, y1, 'up');
    drawArrowHead(ctx, xDim, y2, 'down');
    ctx.restore();
    if (label) {
      drawPaddedLabel(ctx, label, xDim, (y1 + y2) / 2, true);
    }
  }

  function drawPlaceholder(message) {
    if (!canvas || !container) {
      return;
    }

    var ctx = canvas.getContext('2d');
    var box = canvasBox(0.55, 280);
    var cw = box.cw;
    var ch = box.ch;

    setCanvasSize(ctx, cw, ch);
    ctx.fillStyle = '#f7f7f7';
    ctx.fillRect(0, 0, cw, ch);
    ctx.fillStyle = '#666';
    ctx.font = '500 13px system-ui, -apple-system, "Segoe UI", sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(message, cw / 2, ch / 2);

    if (externalEl) {
      externalEl.hidden = true;
      externalEl.textContent = '';
    }

    if (matNoteEl) {
      matNoteEl.hidden = true;
      matNoteEl.textContent = '';
    }

    if (summaryEl) {
      summaryEl.hidden = true;
      summaryEl.textContent = '';
    }
  }

  // Plain-language recap so the customer knows exactly what they're getting.
  function updateSummary(heightIn, widthIn, mat, extH, extW) {
    if (!summaryEl) {
      return;
    }

    var parts = [];

    if (IS_FLOATER) {
      var gapIn = FLOATER_GAP_IN > 0 ? FLOATER_GAP_IN : 0;
      if (gapIn > 0) {
        parts.push(
          'Your ' +
            formatInchesLabel(widthIn) +
            ' \u00d7 ' +
            formatInchesLabel(heightIn) +
            ' canvas floats inside this frame with a ' +
            formatInchesLabel(gapIn) +
            ' gap on each side.'
        );
      } else {
        parts.push(
          'Your ' +
            formatInchesLabel(widthIn) +
            ' \u00d7 ' +
            formatInchesLabel(heightIn) +
            ' canvas sits flush inside this frame (no float gap).'
        );
      }
      parts.push(
        'The finished piece is about ' +
          formatInchesLabel(extW) +
          ' \u00d7 ' +
          formatInchesLabel(extH) +
          ' on your wall.'
      );
      summaryEl.hidden = false;
      summaryEl.textContent = parts.join(' ');
      return;
    }

    if (mat && mat.enabled && mat.valid) {
      var artH = heightIn - mat.borderIn * 2;
      var artW = widthIn - mat.borderIn * 2;
      parts.push(
        'Built for ' +
          formatInchesLabel(artW) +
          ' \u00d7 ' +
          formatInchesLabel(artH) +
          ' artwork with a ' +
          formatInchesLabel(mat.borderIn) +
          ' ' +
          mat.color.name.toLowerCase() +
          ' mat around it.'
      );
      parts.push(
        'About ' +
          formatInchesLabel(mat.windowWidthIn) +
          ' \u00d7 ' +
          formatInchesLabel(mat.windowHeightIn) +
          ' of your picture shows' +
          (mat.overlapIn > 0
            ? ' (mat laps ' + formatInchesLabel(mat.overlapIn) + ' over each edge to hold it).'
            : '.')
      );
      parts.push('Finished size is about ' + formatInchesLabel(extW) + ' \u00d7 ' + formatInchesLabel(extH) + '.');
    } else {
      parts.push(
        'This frame fits ' +
          formatInchesLabel(widthIn) +
          ' \u00d7 ' +
          formatInchesLabel(heightIn) +
          ' artwork.'
      );
      parts.push('Outside size is about ' + formatInchesLabel(extW) + ' \u00d7 ' + formatInchesLabel(extH) + '.');
    }

    summaryEl.hidden = false;
    summaryEl.textContent = parts.join(' ');
  }

  // Paint the floater's canvas as real stretched-canvas texture (used when no
  // artwork is uploaded; an uploaded photo covers it). Uses the photographic
  // weave asset, drawn "cover" so it never distorts; falls back to a simple
  // woven fill if the image hasn't loaded yet.
  function drawCanvasTexture(ctx, x, y, w, h, pxPerIn) {
    ctx.save();
    ctx.fillStyle = '#efeae0';
    ctx.fillRect(x, y, w, h);

    var img = canvasTextureImage;
    if (img && img.complete && img.naturalWidth > 0) {
      ctx.beginPath();
      ctx.rect(x, y, w, h);
      ctx.clip();
      drawCoverImage(ctx, img, x, y, w, h);
    }
    // Until the weave photo loads we simply leave the flat base fill above — no
    // procedural stand-in — so there's no texture "flash" before the real image.
    ctx.restore();
  }

  // Draw an image to "cover" a rect (scale to fill, center-crop). Caller clips.
  function drawCoverImage(ctx, img, x, y, w, h) {
    var iw = img.naturalWidth || img.width;
    var ih = img.naturalHeight || img.height;
    if (!iw || !ih) {
      return;
    }
    var scale = Math.max(w / iw, h / ih);
    var dw = iw * scale;
    var dh = ih * scale;
    ctx.drawImage(img, x + (w - dw) / 2, y + (h - dh) / 2, dw, dh);
  }

  // Overlay the woven canvas texture on top of an uploaded photo so it reads as
  // "printed on canvas": the bright threads leave the image alone (multiply by
  // near-white ~= no change) while the darker weave lines add a subtle texture.
  function overlayCanvasWeave(ctx, x, y, w, h) {
    var img = canvasTextureImage;
    if (!img || !img.complete || !img.naturalWidth) {
      return;
    }
    ctx.save();
    ctx.beginPath();
    ctx.rect(x, y, w, h);
    ctx.clip();
    ctx.globalCompositeOperation = 'multiply';
    ctx.globalAlpha = 0.5;
    drawCoverImage(ctx, img, x, y, w, h);
    ctx.restore();
  }

  // Floater frame: a visible wall + an optional float gap + the canvas. The
  // entered dimensions are the CANVAS size; the frame OPENING is canvas + the
  // selected float gap (0", 1/4", or 1/2") on each side.
  function drawFloater(heightIn, widthIn) {
    var ctx = canvas.getContext('2d');
    var box = canvasBox(0.82, 420);
    var cw = box.cw;
    var ch = box.ch;
    setCanvasSize(ctx, cw, ch);
    ctx.fillStyle = '#f7f7f7';
    ctx.fillRect(0, 0, cw, ch);

    var gapIn = FLOATER_GAP_IN > 0 ? FLOATER_GAP_IN : 0;
    var ring = FLOATER_FACE_IN + gapIn;
    var extW = widthIn + ring * 2;
    var extH = heightIn + ring * 2;
    var openingH = heightIn + gapIn * 2;
    var openingW = widthIn + gapIn * 2;

    // Margins match the standard frame so the dimension bars have room.
    var mLeft = 44;
    var mTop = 30;
    var mRight = 18;
    var mBottom = 18;
    var maxOuterW = cw - mLeft - mRight;
    var maxOuterH = ch - mTop - mBottom;
    var outerAspect = extW / extH;
    var outerW;
    var outerH;
    if (outerAspect > maxOuterW / maxOuterH) {
      outerW = maxOuterW;
      outerH = maxOuterW / outerAspect;
    } else {
      outerH = maxOuterH;
      outerW = maxOuterH * outerAspect;
    }

    var pxPerIn = outerW / extW;
    var facePx = FLOATER_FACE_IN * pxPerIn;
    var gapPx = gapIn * pxPerIn;
    var ox = mLeft + (maxOuterW - outerW) / 2;
    var oy = mTop + (maxOuterH - outerH) / 2;

    // Cast shadow behind the frame so a white/light floater lifts off the light
    // background instead of melting into it (the outer rim is otherwise invisible
    // on a near-white finish). The wall is drawn on top, hiding this fill.
    ctx.save();
    ctx.shadowColor = 'rgba(0, 0, 0, 0.22)';
    ctx.shadowBlur = Math.max(8, facePx * 0.9);
    ctx.shadowOffsetX = Math.max(1, facePx * 0.15);
    ctx.shadowOffsetY = Math.max(3, facePx * 0.4);
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(ox, oy, outerW, outerH);
    ctx.restore();

    // Textured (or representative) visible wall. For floaters we show only the
    // rim (cropped), not the full profile squashed into the face band.
    var texReady =
      mouldingTextureImage && mouldingTextureImage.complete && mouldingTextureImage.naturalWidth > 0;
    if (STRIP_MODE && texReady && !MISSING_TEXTURE) {
      drawFloaterWall(ctx, ox, oy, outerW, outerH, facePx);
    } else {
      drawMouldingFrame(ctx, ox, oy, outerW, outerH, facePx);
    }

    // Hairline outer edge so the rim boundary is always legible regardless of
    // the moulding finish.
    ctx.save();
    ctx.strokeStyle = 'rgba(0, 0, 0, 0.18)';
    ctx.lineWidth = 1;
    ctx.strokeRect(ox + 0.5, oy + 0.5, outerW - 1, outerH - 1);
    ctx.restore();

    var innerX = ox + facePx;
    var innerY = oy + facePx;
    var innerW = outerW - facePx * 2; // frame opening in px (= canvas + gap*2)
    var innerH = outerH - facePx * 2;
    var cx0 = innerX + gapPx;
    var cy0 = innerY + gapPx;
    var canvasW = widthIn * pxPerIn;
    var canvasH = heightIn * pxPerIn;

    // Dark float gap ring — only when a gap is selected. With no spacing the
    // canvas fills the opening edge-to-edge.
    if (gapPx > 0.5) {
      ctx.save();
      ctx.fillStyle = '#1c1c1c';
      ctx.fillRect(innerX, innerY, innerW, innerH);
      ctx.restore();
    }

    drawCanvasTexture(ctx, cx0, cy0, canvasW, canvasH, pxPerIn);
    var hasArtwork = drawArtwork(ctx, cx0, cy0, canvasW, canvasH);
    if (hasArtwork) {
      // Give an uploaded photo a subtle canvas-weave so it looks printed on the
      // floating canvas rather than pasted flat.
      overlayCanvasWeave(ctx, cx0, cy0, canvasW, canvasH);
    }

    // Faint contact shadow around the canvas edge.
    ctx.save();
    ctx.strokeStyle = 'rgba(0, 0, 0, 0.35)';
    ctx.lineWidth = 1;
    ctx.shadowColor = 'rgba(0, 0, 0, 0.4)';
    ctx.shadowBlur = 5;
    ctx.strokeRect(cx0 + 0.5, cy0 + 0.5, canvasW - 1, canvasH - 1);
    ctx.restore();

    if (!hasArtwork) {
      drawCenterLabels(
        ctx,
        formatInchesLabel(widthIn) + ' \u00d7 ' + formatInchesLabel(heightIn),
        'canvas size',
        cx0 + canvasW / 2,
        cy0 + canvasH / 2,
        false
      );
    }

    // Dimension bars show the frame OPENING (canvas + gap on each side).
    drawDimLineH(ctx, innerX, innerX + innerW, innerY, oy - 16, formatInchesLabel(openingW));
    drawDimLineV(ctx, innerY, innerY + innerH, innerX, ox - 28, formatInchesLabel(openingH));

    if (matNoteEl) {
      matNoteEl.hidden = true;
      matNoteEl.textContent = '';
    }
    // Outside size is stated once in the summary; keep the legacy line hidden.
    if (externalEl) {
      externalEl.hidden = true;
      externalEl.textContent = '';
    }
    updateSummary(heightIn, widthIn, null, extH, extW);
  }

  function draw(heightIn, widthIn) {
    if (!canvas || !container) {
      return;
    }

    if (IS_FLOATER) {
      hideFitSuggest();
      hideMatEnlargeNotice();
      drawFloater(heightIn, widthIn);
      return;
    }

    var mat = getMatState(heightIn, widthIn);
    var openingH = mat.openingHeightIn || heightIn;
    var openingW = mat.openingWidthIn || widthIn;
    var ext = externalDimensions(openingH, openingW);

    // Outside size is stated once, in the plain-language summary below the
    // canvas (updateSummary). Keep this legacy line hidden so it isn't repeated.
    if (externalEl) {
      externalEl.hidden = true;
      externalEl.textContent = '';
    }

    if (matNoteEl) {
      if (mat.enabled && mat.valid) {
        // The mat details are stated once in the summary + DPO note below; keep
        // this mid-panel line hidden so we're not repeating the same numbers.
        matNoteEl.hidden = true;
        matNoteEl.textContent = '';
      } else if (mat.enabled && !mat.valid) {
        matNoteEl.hidden = false;
        matNoteEl.textContent =
          'Border is too wide for this frame size. Choose a smaller border.';
      } else {
        matNoteEl.hidden = true;
        matNoteEl.textContent = '';
      }
    }

    updateMatEnlargeNotice(heightIn, widthIn, mat);

    var ctx = canvas.getContext('2d');
    var box = canvasBox(0.82, 420);
    var cw = box.cw;
    var ch = box.ch;

    setCanvasSize(ctx, cw, ch);

    // Asymmetric margins leave room for the architectural dimension lines: a
    // wider left margin for the rotated height label and a taller top margin
    // for the width label.
    var mLeft = 44;
    var mTop = 30;
    var mRight = 18;
    var mBottom = 18;
    var maxOuterW = cw - mLeft - mRight;
    var maxOuterH = ch - mTop - mBottom;
    var outerAspect = ext.width / ext.height;
    var outerW;
    var outerH;

    if (outerAspect > maxOuterW / maxOuterH) {
      outerW = maxOuterW;
      outerH = maxOuterW / outerAspect;
    } else {
      outerH = maxOuterH;
      outerW = maxOuterH * outerAspect;
    }

    var pxPerIn = outerW / ext.width;
    var mouldingPx = MOULDING_WIDTH_IN * pxPerIn;
    var innerW = openingW * pxPerIn;
    var innerH = openingH * pxPerIn;
    var ox = mLeft + (maxOuterW - outerW) / 2;
    var oy = mTop + (maxOuterH - outerH) / 2;
    var ix = ox + mouldingPx;
    var iy = oy + mouldingPx;

    var matOuterLabelH = formatInchesLabel(openingH);
    var matOuterLabelW = formatInchesLabel(openingW);
    var windowHeightIn = mat.windowHeightIn;
    var windowWidthIn = mat.windowWidthIn;
    var windowLabelH = formatInchesLabel(windowHeightIn);
    var windowLabelW = formatInchesLabel(windowWidthIn);

    var contentX = ix;
    var contentY = iy;
    var contentW = innerW;
    var contentH = innerH;
    var borderPx = 0;

    if (mat.enabled && mat.valid) {
      borderPx = mat.borderIn * pxPerIn;
      contentX = ix + borderPx;
      contentY = iy + borderPx;
      contentW = innerW - borderPx * 2;
      contentH = innerH - borderPx * 2;
    }

    var centerX = ix + innerW / 2;
    var centerY = iy + innerH / 2;
    var windowCenterX = contentX + contentW / 2;
    var windowCenterY = contentY + contentH / 2;

    ctx.fillStyle = '#f7f7f7';
    ctx.fillRect(0, 0, cw, ch);

    drawMouldingFrame(ctx, ox, oy, outerW, outerH, mouldingPx);

    if (mat.enabled && mat.valid) {
      drawMatWithWindow(
        ctx,
        ix,
        iy,
        innerW,
        innerH,
        contentX,
        contentY,
        contentW,
        contentH,
        mat.color.hex
      );
    } else {
      ctx.fillStyle = ARTWORK_WINDOW_FILL;
      ctx.fillRect(ix, iy, innerW, innerH);
    }

    var hasArtwork = drawArtwork(ctx, contentX, contentY, contentW, contentH);

    // Soft rabbet shadow at the frame opening (depth cue, not a hard splice line).
    ctx.save();
    ctx.strokeStyle = 'rgba(0, 0, 0, 0.22)';
    ctx.lineWidth = 1;
    ctx.shadowColor = 'rgba(0, 0, 0, 0.28)';
    ctx.shadowBlur = 5;
    ctx.shadowOffsetX = 0;
    ctx.shadowOffsetY = 0;
    ctx.strokeRect(ix + 0.5, iy + 0.5, innerW - 1, innerH - 1);
    ctx.restore();

    if (mat.enabled && mat.valid) {
      ctx.save();
      ctx.strokeStyle = 'rgba(0, 0, 0, 0.16)';
      ctx.lineWidth = 1;
      ctx.shadowColor = 'rgba(0, 0, 0, 0.2)';
      ctx.shadowBlur = 4;
      ctx.strokeRect(contentX + 0.5, contentY + 0.5, contentW - 1, contentH - 1);
      ctx.restore();
    }

    // Center caption only when there's no uploaded artwork (never white text
    // over an image). Dark text sits on the light window fill so it stays
    // readable even with a white or black mat.
    if (!hasArtwork) {
      if (mat.enabled && mat.valid) {
        // Kit: keep the customer's artwork size in the hole so a 5x5 + 1" mat
        // still reads as 5x5 art, not a shrunken "visible" window.
        var holeLabelH = kitOwnsArtworkSize()
          ? formatInchesLabel(mat.artHeightIn)
          : windowLabelH;
        var holeLabelW = kitOwnsArtworkSize()
          ? formatInchesLabel(mat.artWidthIn)
          : windowLabelW;
        drawCenterLabels(
          ctx,
          holeLabelW + ' \u00d7 ' + holeLabelH,
          kitOwnsArtworkSize() ? 'your artwork' : 'visible artwork',
          windowCenterX,
          windowCenterY,
          false
        );
      } else {
        drawCenterLabels(
          ctx,
          matOuterLabelW + ' \u00d7 ' + matOuterLabelH,
          'your artwork',
          centerX,
          centerY,
          false
        );
      }
    }

    // Architectural dimension bars in the margins: the frame opening width
    // (top) and height (left) — the size we build the frame to fit. Drawn on
    // the light margin with dark labels so they're always legible, unlike the
    // old on-frame text.
    drawDimLineH(ctx, ix, ix + innerW, iy, oy - 16, matOuterLabelW);
    drawDimLineV(ctx, iy, iy + innerH, ix, ox - 28, matOuterLabelH);

    updateSummary(openingH, openingW, mat, ext.height, ext.width);
  }

  function setGalleryPreviewMode(active) {
    if (galleryEl) {
      galleryEl.classList.toggle('ff-frame-preview-active', active);
    }
  }

  function setBadgeText(text) {
    if (badgeEl) {
      badgeEl.textContent = text;
    }
  }

  function getThumbnailImageUrl(thumbnail) {
    var href = thumbnail.getAttribute('href');

    if (href && href !== '#') {
      return href;
    }

    var img = thumbnail.querySelector('img');

    if (!img) {
      return null;
    }

    return img.currentSrc || img.src || null;
  }

  function showProductPhoto(imageUrl, thumbnail) {
    if (!productPhotoEl || !productPhotoImg || !canvas) {
      return;
    }

    showingProductPhoto = true;
    productPhotoImg.src = imageUrl;
    productPhotoEl.hidden = false;
    canvas.classList.add('ff-frame-preview__canvas--hidden');

    if (externalEl) {
      externalEl.hidden = true;
    }

    if (matNoteEl) {
      matNoteEl.hidden = true;
    }

    if (summaryEl) {
      summaryEl.hidden = true;
    }

    setBadgeText('Product photo');

    if (galleryEl && thumbnail) {
      galleryEl.querySelectorAll('.thumbnails .thumbnail').forEach(function (thumb) {
        thumb.classList.remove('ff-thumbnail-selected', 'selected');
      });
      thumbnail.classList.add('ff-thumbnail-selected', 'selected');
    }
  }

  function hideProductPhoto() {
    if (!productPhotoEl || !canvas) {
      return;
    }

    showingProductPhoto = false;
    productPhotoEl.hidden = true;
    productPhotoImg.removeAttribute('src');
    canvas.classList.remove('ff-frame-preview__canvas--hidden');
    setBadgeText(MISSING_TEXTURE ? 'Representative preview' : 'Size preview');

    if (galleryEl) {
      galleryEl.querySelectorAll('.thumbnails .thumbnail').forEach(function (thumb) {
        thumb.classList.remove('ff-thumbnail-selected');
      });
    }

    redraw();
  }

  function onThumbnailClick(event) {
    if (!galleryEl || !galleryEl.classList.contains('ff-frame-preview-active')) {
      return;
    }

    var thumbnail = event.target.closest('.thumbnails .thumbnail');

    if (!thumbnail || !galleryEl.contains(thumbnail)) {
      return;
    }

    var imageUrl = getThumbnailImageUrl(thumbnail);

    if (!imageUrl) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();

    showProductPhoto(imageUrl, thumbnail);
  }

  function bindProductThumbnails() {
    if (!galleryEl) {
      return;
    }

    galleryEl.addEventListener('click', onThumbnailClick, true);

    if (productPhotoBackBtn) {
      productPhotoBackBtn.addEventListener('click', function () {
        hideProductPhoto();
      });
    }
  }

  function show() {
    if (container) {
      container.hidden = false;
      container.classList.add('ff-frame-preview--visible');
    }
  }

  function onResize() {
    if ((!showingProductPhoto || zoomMode === 'canvas') && lastHeight && lastWidth) {
      draw(lastHeight, lastWidth);
    }
  }

  function bindResize() {
    window.addEventListener('resize', function () {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(onResize, 150);
    });
  }

  window.FFFrameRenderer = {
    init: function (selector) {
      container = document.querySelector(selector);

      if (!container) {
        return false;
      }

      canvas = container.querySelector('canvas');
      badgeEl = container.querySelector('.ff-frame-preview__badge');
      externalEl = container.querySelector('.ff-frame-preview__external');
      matNoteEl = document.getElementById('ff-frame-mat-note');
      productPhotoEl = document.getElementById('ff-frame-product-photo');
      productPhotoImg = document.getElementById('ff-frame-product-photo-img');
      productPhotoBackBtn = document.getElementById('ff-frame-product-photo-back');
      galleryEl = document.querySelector('.gallery.ff-frame-preview-enabled');
      fileInput = container.querySelector('.ff-frame-preview__upload-input');
      removeBtn = container.querySelector('.ff-frame-preview__remove-btn');
      fitCheckbox = document.getElementById('ff-frame-fit-toggle');
      fitSuggestEl = document.getElementById('ff-frame-fit-suggest');
      fitSuggestTextEl = document.getElementById('ff-frame-fit-suggest-text');
      fitApplyBtn = document.getElementById('ff-frame-fit-apply');
      matCheckbox = document.getElementById('ff-frame-mat-enabled');
      matOptionsWrap = document.getElementById('ff-frame-mat-options-wrap');
      matBorderSelect = document.getElementById('ff-frame-mat-border');
      matColorSelect = document.getElementById('ff-frame-mat-color');
      matEnlargeEl = document.getElementById('ff-frame-mat-enlarge');
      matEnlargeTextEl = document.getElementById('ff-frame-mat-enlarge-text');
      matEnlargeBtn = document.getElementById('ff-frame-mat-enlarge-btn');
      matOverlapCheckbox = document.getElementById('ff-frame-mat-overlap');
      summaryEl = document.getElementById('ff-frame-summary');

      if (!canvas) {
        return false;
      }

      MOULDING_WIDTH_IN = roundTo16(parseMouldingWidth(container.getAttribute('data-moulding-width')));
      STRIP_MODE = (container.getAttribute('data-moulding-format') || '') === 'strip';
      NO_MIRROR = container.getAttribute('data-no-mirror') === 'true';
      IS_FLOATER = container.getAttribute('data-is-floater') === 'true';
      MISSING_TEXTURE = container.getAttribute('data-moulding-missing') === 'true';
      var floaterFaceAttr = parseFloat(container.getAttribute('data-floater-face'));
      if (floaterFaceAttr > 0) {
        FLOATER_FACE_IN = floaterFaceAttr;
      }
      var lipAttr = parseFloat((container.getAttribute('data-frame-lip') || '').replace(/[^\d.]/g, ''));
      if (lipAttr > 0) {
        FRAME_LIP_IN = lipAttr;
      }
      if (MISSING_TEXTURE) {
        setBadgeText('Representative preview');
      }

      // Floater frames don't take a mat — hide the matboard controls entirely
      // and make sure the mat state is off.
      if (IS_FLOATER) {
        var matControls = document.getElementById('ff-frame-mat-controls');
        if (matControls) {
          matControls.hidden = true;
        }
        if (matCheckbox) {
          matCheckbox.checked = false;
        }
      }

      loadMouldingTexture(container.getAttribute('data-moulding-texture'));
      loadCanvasTexture(container.getAttribute('data-canvas-texture'));

      bindResize();
      bindArtworkUpload();
      bindMatControls();
      bindProductThumbnails();
      bindZoom();
      setBadgeText(MISSING_TEXTURE ? 'Representative preview' : 'Size preview');
      return true;
    },

    update: function (heightIn, widthIn) {
      if (!canvas || !container) {
        return;
      }

      if (!heightIn || !widthIn || heightIn <= 0 || widthIn <= 0) {
        lastHeight = null;
        lastWidth = null;
        setGalleryPreviewMode(false);
        return;
      }

      show();

      // Track which dimension the customer changed so the suggestion can anchor
      // on it and recompute the other side.
      if (lastHeight !== null && Math.abs(heightIn - lastHeight) > 0.001) {
        lastChangedDim = 'height';
      } else if (lastWidth !== null && Math.abs(widthIn - lastWidth) > 0.001) {
        lastChangedDim = 'width';
      }

      if (skipProgrammaticDimUpdate) {
        skipProgrammaticDimUpdate = false;
      } else {
        // A real customer edit. In artwork mode, with a mat on, the entered size
        // is the frame opening, so the artwork behind it is opening - border on
        // each side; track that as the base to revert to / keep growing from.
        // In opening mode the entered size is a fixed opening we never grow, so
        // we don't track a mat base (getMatState insets from the entered size).
        var borderNow = currentBorderIn();

        if (usesEnlarge() && isMatSelected() && borderNow > 0 && !kitOwnsArtworkSize()) {
          matBaseH = Math.max(1, heightIn - borderNow * 2);
          matBaseW = Math.max(1, widthIn - borderNow * 2);
          setDesiredVisible(matBaseH, matBaseW);
        } else if (lastChangedDim && artworkImage) {
          setDesiredVisible(heightIn, widthIn);
        }
      }

      lastHeight = heightIn;
      lastWidth = widthIn;
      setGalleryPreviewMode(true);

      // Recompute the no-white-space suggestion live as dimensions change, so it
      // follows whichever side the customer just changed.
      updateSizeSuggestion();

      if (!showingProductPhoto) {
        draw(heightIn, widthIn);
      }
    },

    hideProductPhoto: hideProductPhoto,
    clearArtwork: clearArtwork,

    // How the entered dimensions are interpreted: 'artwork' (mat grows the
    // opening) or 'opening' (opening fixed, mat insets). Display-only.
    getSizeBasis: function () {
      return SIZE_BASIS;
    },

    setSizeBasis: function (basis) {
      var next = basis === 'opening' ? 'opening' : 'artwork';
      if (next === SIZE_BASIS) {
        return;
      }
      SIZE_BASIS = next;

      // Reconcile a mat that's already on so the switch is coherent:
      //  - to 'opening': undo any growth so the opening is fixed at the size the
      //    customer entered (matBase); the mat then insets within it.
      //  - to 'artwork': re-grow the frame to fit the captured artwork + mat.
      if (isMatSelected() && matBaseH && matBaseW) {
        if (SIZE_BASIS === 'opening') {
          setOrderedFrameInches(matBaseH, matBaseW);
          matBaseH = null;
          matBaseW = null;
          resetDesiredVisible();
        } else {
          setDesiredVisible(matBaseH, matBaseW);
          growFrameForMat();
        }
      } else if (SIZE_BASIS === 'artwork' && isMatSelected() && lastHeight && lastWidth) {
        // Mat is on but we weren't tracking a base (entered in opening mode);
        // treat the current opening's inner art as the base and grow.
        var b = currentBorderIn();
        matBaseH = Math.max(1, lastHeight - b * 2);
        matBaseW = Math.max(1, lastWidth - b * 2);
        setDesiredVisible(matBaseH, matBaseW);
        growFrameForMat();
      }

      redraw();
      refreshDimensionSummary();
    },

    // Current matboard context for the entered opening size, so the dimension
    // summary ("green box") can explain that the frame is built to the mat's
    // outer size and state the resulting visible artwork window. Returns null
    // when no valid mat is applied.
    getMatContextFor: function (heightIn, widthIn) {
      if (IS_FLOATER || !heightIn || !widthIn) {
        return null;
      }

      var mat = getMatState(heightIn, widthIn);

      if (!mat.enabled || !mat.valid) {
        return null;
      }

      // The artwork that actually fits = opening minus the mat border on each
      // side (window + the overlap the mat laps back over the art). This is the
      // size the customer's art should be, which the visible window understates
      // by the overlap.
      var artFitHeightIn = mat.artHeightIn != null ? mat.artHeightIn : heightIn - mat.borderIn * 2;
      var artFitWidthIn = mat.artWidthIn != null ? mat.artWidthIn : widthIn - mat.borderIn * 2;
      var openingH = mat.openingHeightIn != null ? mat.openingHeightIn : heightIn;
      var openingW = mat.openingWidthIn != null ? mat.openingWidthIn : widthIn;
      var ext = externalDimensions(openingH, openingW);

      return {
        on: true,
        basis: kitOwnsArtworkSize() ? 'artwork' : SIZE_BASIS,
        borderLabel: formatInchesLabel(mat.borderIn),
        colorName: mat.color.name,
        hasOverlap: mat.overlapIn > 0,
        overlapLabel: mat.overlapIn > 0 ? formatInchesLabel(mat.overlapIn) : null,
        windowHeightLabel: formatInchesLabel(mat.windowHeightIn),
        windowWidthLabel: formatInchesLabel(mat.windowWidthIn),
        artFitHeightLabel: formatInchesLabel(artFitHeightIn),
        artFitWidthLabel: formatInchesLabel(artFitWidthIn),
        openingHeightLabel: formatInchesLabel(openingH),
        openingWidthLabel: formatInchesLabel(openingW),
        finishedHeightLabel: formatInchesLabel(ext.height),
        finishedWidthLabel: formatInchesLabel(ext.width)
      };
    },

    // Set the float gap (inches) fed from the storefront's "spacing" option.
    // 0 = no spacing (canvas flush), 0.25, or 0.5.
    setFloaterGap: function (gapIn) {
      var g = typeof gapIn === 'number' && gapIn >= 0 ? gapIn : FLOATER_GAP_IN;
      if (g === FLOATER_GAP_IN) {
        return;
      }
      FLOATER_GAP_IN = g;
      if (IS_FLOATER) {
        redraw();
      }
    },

    // Floater context for the entered canvas size, so the dimension summary can
    // explain that the frame opening = canvas + the selected gap on each side,
    // and warn when "no spacing" is chosen. Returns null for non-floaters.
    getFloaterContextFor: function (heightIn, widthIn) {
      if (!IS_FLOATER || !heightIn || !widthIn) {
        return null;
      }
      var gapIn = FLOATER_GAP_IN > 0 ? FLOATER_GAP_IN : 0;
      return {
        on: true,
        gapIn: gapIn,
        noSpacing: gapIn <= 0,
        gapLabel: formatInchesLabel(gapIn),
        openingHeightLabel: formatInchesLabel(heightIn + gapIn * 2),
        openingWidthLabel: formatInchesLabel(widthIn + gapIn * 2)
      };
    }
  };
})(window);
